import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search,
  Filter,
  ChevronDown,
  Grid3X3,
  List,
  SlidersHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from '@/lib/utils';

interface MarketplaceFiltersProps {
  readonly searchQuery: string;
  readonly setSearchQuery: (query: string) => void;
  readonly viewMode: "grid" | "list";
  readonly setViewMode: (mode: "grid" | "list") => void;
  readonly selectedType: string;
  readonly setSelectedType: (type: string) => void;
  readonly deckTypes: readonly string[];
  readonly onSort?: (sortType: string) => void;
}

export function MarketplaceFilters({
  searchQuery,
  setSearchQuery,
  viewMode,
  setViewMode,
  selectedType,
  setSelectedType,
  deckTypes,
  onSort
}: Readonly<MarketplaceFiltersProps>) {
  return (
    <div className="flex flex-col md:flex-row gap-4 mb-6">
      {/* Search */}
      <div className="relative flex-grow">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search decks..."
          className="pl-10 bg-black/30 border-primary/30 focus:border-primary"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>
      
      {/* View mode and filters */}
      <div className="flex gap-2">
        <div className="flex gap-2">
          <Button
            variant={viewMode === "grid" ? "default" : "outline"}
            size="icon"
            className={
              viewMode === "grid"
                ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md border-0"
                : "bg-black/40 border-blue-500/40 text-white hover:bg-blue-600/20 hover:border-blue-500/60"
            }
            onClick={() => setViewMode("grid")}
          >
            <Grid3X3 className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === "list" ? "default" : "outline"}
            size="icon"
            className={
              viewMode === "list"
                ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md border-0"
                : "bg-black/40 border-blue-500/40 text-white hover:bg-blue-600/20 hover:border-blue-500/60"
            }
            onClick={() => setViewMode("list")}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Filters */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="bg-black/40 border-green-500/40 text-white">
              <Filter className="h-4 w-4 mr-2" />
              <span>Type</span>
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-gray-900 border-gray-700">
            <DropdownMenuItem 
              className={cn("cursor-pointer", selectedType === "all" && "bg-primary/20")}
              onClick={() => setSelectedType("all")}
            >
              All Types
            </DropdownMenuItem>
            {deckTypes.map((type) => (
              <DropdownMenuItem 
                key={type}
                className={cn("cursor-pointer capitalize", selectedType === type.toLowerCase() && "bg-primary/20")}
                onClick={() => setSelectedType(type.toLowerCase())}
              >
                {type}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        
        {/* Sort */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="bg-black/40 border-green-500/40 text-white">
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              <span>Sort</span>
              <ChevronDown className="h-4 w-4 ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-gray-900 border-gray-700">
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => onSort?.('newest')}
            >
              Newest First
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => onSort?.('alphabetical')}
            >
              Alphabetical
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => onSort?.('price-asc')}
            >
              Price (Low to High)
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="cursor-pointer"
              onClick={() => onSort?.('price-desc')}
            >
              Price (High to Low)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}